import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { useGetParkingStatus } from 'src/features/parkingManagement/api/useGetParkingStatus';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { Badge } from 'src/registry/ui/Badge';
import { generalMessages } from 'src/utils/messages';

import type { CellContext } from '@tanstack/react-table';
import type { BadgeOptions } from 'src/registry/types/badges';

interface ParkingStatusProps<TData, TValue> extends CellContext<TData, TValue> {
    parkingId: string;
}

export function ParkingStatus<TData, TValue>({ parkingId }: ParkingStatusProps<TData, TValue>) {
    const { formatMessage } = useIntl();

    const { data, isLoading } = useGetParkingStatus({
        parkingId,
        refetch: false,
        config: {
            enabled: !!parkingId,
        },
    });

    const status = data?.available === undefined ? false : data?.available === null ? 'null' : data?.available;

    const badgeOptions: BadgeOptions = {
        true: {
            theme: 'green',
            label: generalMessages.online,
        },
        false: {
            theme: 'red',
            label: generalMessages.offline,
        },
        null: {
            theme: 'gray',
            label: generalMessages.notApplicable,
        },
    };

    return (
        <>
            {isLoading ? (
                <Badge theme="gray">
                    <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="size-4 animate-spin" />
                    <div>{formatMessage(generalMessages.loading)}</div>
                </Badge>
            ) : null}
            {!isLoading && (
                <Badge theme={badgeOptions[status.toString()].theme}>
                    {formatMessage(badgeOptions[status.toString()].label)}
                </Badge>
            )}
        </>
    );
}
